import React, { useEffect, useState } from "react"
import {
  Row, Col, CardHeader, CardBody, Spinner, Alert, Input, Button,
  Modal, ModalHeader, ModalBody, ModalFooter,
  FormGroup, Label, FormFeedback,
} from "reactstrap"
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table"
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css"
import Select from "react-select"
import { useFormik } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"
import {
  getAllAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
} from "../../api/requests/announcement-requests"
import { SubscriptionDeleteModal } from "../modals/subscription/SubscriptionDeleteModal"
import {
  ANNOUNCEMENT_DEFAULT_ICONS,
  ANNOUNCEMENT_ICON_CHOICES,
  ANNOUNCEMENT_SEVERITY_COLORS,
} from "../../constants/announcements"
import DateTimeInput from "../../components/Common/DateTimeInput";
import AnnouncementTabs from "./AnnouncementTabs"

const TableCard = ({ className = "", children, ...rest }) => (
  <div
    className={`card rounded-4 border-0 shadow-lg ${className}`}
    style={{ boxShadow: "0 .5rem 1.25rem rgba(10,49,110,.08)" }}
    {...rest}
  >
    {children}
  </div>
)

const NAVY = "#221C5D"

const severityOptions = [
  { value: "INFO", label: "Bilgi" },
  { value: "SUCCESS", label: "Olumlu" },
  { value: "WARNING", label: "Uyarı" },
]

const targetScopeOptions = [
  { value: "ALL", label: "Herkes" },
  { value: "COMPANY_USERS", label: "Firma kullanıcıları" },
  { value: "ACCOUNT_USERS", label: "Cari/bayi kullanıcıları" },
]

const severityLabel = (v) => severityOptions.find((o) => o.value === v)?.label || v
const scopeLabel = (v) => targetScopeOptions.find((o) => o.value === v)?.label || v

/**
 * datetime-local input'u ile backend'in LocalDateTime'ı arasındaki çeviri.
 * Backend saniyesiz "2026-08-26T14:30" biçimini de kabul ediyor; okurken
 * saniye/milisaniye kısmı kesilir, yazarken boş değer null'a döner.
 */
const toInputDateTime = (v) => (v ? String(v).slice(0, 16) : "")
const fromInputDateTime = (v) => (v ? v : null)

const formatDateTime = (v) => {
  if (!v) return "—"
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })
}

const Announcements = () => {
  const [announcements, setAnnouncements] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [saving, setSaving] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [submitError, setSubmitError] = useState("")

  const styles = `
    .announcements-modern .table thead tr { background-color: #f9f9f9; }
    .announcements-modern .table th { border: 0; color: #6c757d; font-weight: 600; }
    .announcements-modern .table td { vertical-align: middle; }
    .announcements-modern .table-hover tbody tr:hover { background: #fcfcfd; }
    .announcements-modern .search-input { width: 250px; }
    .announcements-modern .btn-rounded { border-radius: 999px; }
    .announcements-modern .btn-navy { background: ${NAVY}; border-color: ${NAVY}; color: #fff; }
    .announcements-modern .btn-navy:hover { filter: brightness(1.03); }
    .announcements-modern .action-btn { border-radius: 999px; padding: .35rem .6rem; }
    .announcements-modern .pill-input { border-radius: 999px !important; padding: .45rem .9rem; }
    /* Tek satırda akan kompakt ızgara: grup başlıkları her biri tam satır
       kapladığı için seçici gereksiz uzuyordu, gruplar sabitte kaldı ama
       ekranda düzleştirildi. */
    .icon-picker { display: flex; flex-wrap: wrap; gap: .3rem; }
    .icon-picker .icon-choice {
      width: 32px; height: 32px;
      display: flex; align-items: center; justify-content: center;
      border: 1px solid #e6e8ee; border-radius: 8px;
      background: #fff; cursor: pointer; padding: 0;
      transition: border-color .15s, background .15s;
    }
    .icon-picker .icon-choice:hover { border-color: #c9cfdb; background: #f7f8fa; }
    .icon-picker .icon-choice.is-selected { border-width: 2px; }
    .icon-picker .icon-sep { width: 1px; background: #e6e8ee; margin: 0 .2rem; }
    /* Uzun başlık/metin sütunu aşmasın. Ölçü <td>'ye değil içindeki div'e
       verilir: auto-layout tabloda hücrenin max-width'i yok sayılıyor.
       .ann-text'teki min-width:0 şart — flex öğesi varsayılan olarak
       içeriğinden dar olamaz, onsuz üç satır da kesilmez. (Bootstrap'te
       min-width-0 diye bir yardımcı sınıf YOK, elle yazılıyor.) */
    .announcements-modern .ann-cell { max-width: 560px; }
    .announcements-modern .ann-text { min-width: 0; flex: 1 1 auto; }
    .announcements-modern .ann-line {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    @media (max-width: 576px) {
      .announcements-modern .ann-cell { max-width: 100%; }
      .announcements-modern .search-input { width: 100%; }
      .announcements-modern .table { font-size: 12px; }
      .announcements-modern .table td, .announcements-modern .table th { padding: .5rem; }
    }
  `

  document.title = "Duyurular | Tahsilat360"

  const loadData = async (first = false) => {
    try {
      setError(null)
      if (first) setLoading(true); else setRefreshing(true)
      const res = await getAllAnnouncements()
      setAnnouncements(res.data || [])
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    loadData(true)
  }, [])

  const filtered = announcements.filter((a) => {
    if (!searchTerm) return true
    const s = searchTerm.toLowerCase()
    return a.title?.toLowerCase().includes(s) || a.body?.toLowerCase().includes(s)
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: editing?.title || "",
      body: editing?.body || "",
      icon: editing?.icon || "",
      severity: editing?.severity || "INFO",
      actionLabel: editing?.actionLabel || "",
      actionUrl: editing?.actionUrl || "",
      startsAt: toInputDateTime(editing?.startsAt),
      endsAt: toInputDateTime(editing?.endsAt),
      targetScope: editing?.targetScope || "ALL",
      sortOrder: editing?.sortOrder ?? 0,
      active: editing?.active ?? true,
    },
    // Kurallar backend'deki AnnouncementRequestDTO ile birebir aynı; buradaki
    // kopya kullanıcıya isteği göndermeden geri bildirim vermek için.
    validationSchema: Yup.object({
      title: Yup.string().max(150, "Başlık en fazla 150 karakter olabilir").required("Başlık gerekli"),
      body: Yup.string().max(1000, "Metin en fazla 1000 karakter olabilir").required("Duyuru metni gerekli"),
      icon: Yup.string().nullable()
        .matches(/^bx-[a-z0-9-]+$/, { message: "İkon 'bx-' ile başlamalı (ör. bx-broadcast)", excludeEmptyString: true }),
      severity: Yup.string().oneOf(["INFO", "SUCCESS", "WARNING"]).required("Önem derecesi gerekli"),
      // Çift kuralı .when() ile YAZILMAZ: iki alan birbirine when ile bağlanınca
      // Yup bağımlılık grafiğinde döngü görüp "Cyclic dependency" fırlatıyor
      // (excludes argümanı da kurtarmıyor). .test() grafiğe düğüm eklemez,
      // this.parent ile diğer alan yine okunur.
      actionLabel: Yup.string().max(60, "Buton etiketi en fazla 60 karakter olabilir")
        .test("action-pair", "Adres girildiyse buton etiketi de gerekli", function (value) {
          return !this.parent.actionUrl || !!value
        }),
      actionUrl: Yup.string().max(500)
        .matches(/^(\/|https?:\/\/).*/, { message: "Adres '/' ile başlamalı ya da http(s):// olmalı", excludeEmptyString: true })
        .test("action-pair", "Etiket girildiyse adres de gerekli", function (value) {
          return !this.parent.actionLabel || !!value
        }),
      endsAt: Yup.string().nullable().test(
        "after-start",
        "Bitiş tarihi başlangıçtan sonra olmalı",
        function (value) {
          const { startsAt } = this.parent
          if (!value || !startsAt) return true
          return new Date(value) > new Date(startsAt)
        }
      ),
      targetScope: Yup.string().oneOf(["ALL", "COMPANY_USERS", "ACCOUNT_USERS"]).required("Hedef kitle gerekli"),
      sortOrder: Yup.number().min(0, "Sıra değeri negatif olamaz").required("Sıra gerekli"),
    }),
    onSubmit: async (values) => {
      setSaving(true)
      setSubmitError("")
      try {
        const payload = {
          ...values,
          icon: values.icon || null,
          actionLabel: values.actionLabel || null,
          actionUrl: values.actionUrl || null,
          startsAt: fromInputDateTime(values.startsAt),
          endsAt: fromInputDateTime(values.endsAt),
        }
        if (editing) {
          await updateAnnouncement(editing.id, payload)
          toast.success("Duyuru güncellendi")
        } else {
          await createAnnouncement(payload)
          toast.success("Duyuru oluşturuldu")
        }
        closeModal()
        loadData()
      } catch (err) {
        const msg = err.message || "İşlem başarısız oldu"
        setSubmitError(msg)
        toast.error(msg)
      } finally {
        setSaving(false)
      }
    },
  })

  const handleDelete = async (id) => {
    try {
      await deleteAnnouncement(id)
      toast.success("Duyuru silindi")
      loadData()
    } catch (err) {
      toast.error(err.message)
    } finally {
      setDeleteModalOpen(false)
      setDeleteTarget(null)
    }
  }

  const openCreate = () => {
    setEditing(null); formik.resetForm(); setSubmitError(""); setModalOpen(true)
  }
  const openEdit = (a) => {
    setEditing(a); setSubmitError(""); setModalOpen(true)
  }
  const closeModal = () => {
    setModalOpen(false); setEditing(null); formik.resetForm(); setSubmitError("")
  }

  const err = (field) => formik.touched[field] && formik.errors[field]

  const previewSeverity = ANNOUNCEMENT_SEVERITY_COLORS[formik.values.severity] ? formik.values.severity : "INFO"
  const previewAccent = ANNOUNCEMENT_SEVERITY_COLORS[previewSeverity]
  const previewIcon = formik.values.icon || ANNOUNCEMENT_DEFAULT_ICONS[previewSeverity]

  return (
    <div className="page-content announcements-modern">
      <style>{styles}</style>
      <div className="container-fluid">
        <AnnouncementTabs />
        <Row>
          <Col>
            <TableCard>
              <CardHeader className="bg-white border-0 rounded-top-4">
                <div className="d-none d-md-block">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <h5 className="mb-1 fw-bold text-dark">Duyurular</h5>
                      <p className="mb-0 text-muted" style={{ fontSize: ".9rem" }}>
                        {announcements.length > 0
                          ? `Toplam ${announcements.length} duyuru — ${announcements.filter((a) => a.visibleNow).length} tanesi yayında`
                          : "Duyuru bulunamadı"}
                      </p>
                    </div>
                    <Button className="d-flex align-items-center gap-1 btn-rounded btn-navy" onClick={openCreate}>
                      <i className="bx bx-plus" /> Yeni Duyuru
                    </Button>
                  </div>
                  <div className="d-flex justify-content-end">
                    <Input type="search" placeholder="Duyuru ara..." value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)} className="search-input pill-input" />
                  </div>
                </div>
                <div className="d-md-none">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0 fw-semibold">Duyurular</h5>
                    <Button size="sm" className="d-flex align-items-center gap-1 btn-rounded btn-navy" onClick={openCreate}>
                      <i className="bx bx-plus" /> Ekle
                    </Button>
                  </div>
                  <Input type="search" placeholder="Duyuru ara..." value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)} className="pill-input mb-2" />
                </div>
              </CardHeader>

              <CardBody className="pt-2">
                {error && (
                  <Alert color="danger" className="mb-3 rounded-4">
                    <i className="bx bx-error-circle me-2" />{error}
                    <button className="btn btn-sm btn-outline-danger ms-2 btn-rounded" onClick={() => loadData()}>Tekrar Dene</button>
                  </Alert>
                )}

                {loading ? (
                  <div className="text-center p-5">
                    <Spinner size="lg" color="primary" />
                    <h6 className="mt-3 text-muted">Duyurular yükleniyor...</h6>
                  </div>
                ) : (
                  <>
                    <div className={`d-flex justify-content-between align-items-center mb-2 ${refreshing ? "opacity-50" : ""}`}>
                      <div className="text-muted" style={{ fontSize: ".9rem" }}>
                        {filtered.length > 0 && <>Toplam <strong>{filtered.length}</strong> kayıt</>}
                      </div>
                      <button className="btn btn-light btn-sm btn-rounded" onClick={() => loadData()} title="Yenile">
                        <i className={`bx bx-refresh ${refreshing ? "bx-spin" : ""}`} />
                      </button>
                    </div>

                    <div className="table-responsive rounded-4 border">
                      <Table className="table table-hover mb-0">
                        <Thead>
                          <Tr>
                            <Th>Sıra</Th>
                            <Th>Duyuru</Th>
                            <Th className="d-none d-md-table-cell">Önem</Th>
                            <Th className="d-none d-lg-table-cell">Hedef</Th>
                            <Th className="d-none d-lg-table-cell">Yayın Aralığı</Th>
                            <Th>Durum</Th>
                            <Th className="text-end">İşlemler</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {filtered.length ? filtered.map((a) => {
                            const accent = ANNOUNCEMENT_SEVERITY_COLORS[a.severity] || ANNOUNCEMENT_SEVERITY_COLORS.INFO
                            return (
                              <Tr key={a.id} className="border-bottom">
                                <Td><span className="fw-bold text-muted">{a.sortOrder}</span></Td>
                                <Td>
                                  <div className="ann-cell d-flex align-items-center gap-2">
                                    <i className={`bx ${a.icon || ANNOUNCEMENT_DEFAULT_ICONS[a.severity] || ANNOUNCEMENT_DEFAULT_ICONS.INFO}`}
                                      style={{ color: accent, fontSize: "1.1rem", flexShrink: 0 }} />
                                    <div className="ann-text">
                                      <div className="fw-semibold text-dark ann-line" title={a.title}>{a.title}</div>
                                      <div className="text-muted small ann-line" title={a.body}>{a.body}</div>
                                      {a.actionUrl && (
                                        <div className="small ann-line" style={{ color: NAVY }} title={`${a.actionLabel} → ${a.actionUrl}`}>
                                          <i className="bx bx-link-alt me-1" />{a.actionLabel} → {a.actionUrl}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </Td>
                                <Td className="d-none d-md-table-cell">
                                  <span className="badge border-0" style={{ borderRadius: 999, backgroundColor: `${accent}22`, color: accent }}>
                                    {severityLabel(a.severity)}
                                  </span>
                                </Td>
                                <Td className="d-none d-lg-table-cell small">{scopeLabel(a.targetScope)}</Td>
                                <Td className="d-none d-lg-table-cell small">
                                  {formatDateTime(a.startsAt)} / {formatDateTime(a.endsAt)}
                                </Td>
                                <Td>
                                  {/* visibleNow sunucudan geliyor: aktiflik + tarih penceresinin
                                      birlikte sonucu. Pasif ve "süresi dışında" ayrımı korunur. */}
                                  <span className="badge border-0" style={{
                                    borderRadius: 999,
                                    backgroundColor: a.visibleNow ? "#71C30B" : (a.active ? "#B0B7C3" : "#E07A7A"),
                                    color: "#fff",
                                  }}>
                                    {a.visibleNow ? "Yayında" : (a.active ? "Süresi dışında" : "Pasif")}
                                  </span>
                                </Td>
                                <Td className="text-end">
                                  <div className="d-flex gap-1 justify-content-end flex-wrap">
                                    <button className="btn btn-sm action-btn btn-navy" title="Düzenle" onClick={() => openEdit(a)}>
                                      <i className="bx bx-edit" />
                                    </button>
                                    <button className="btn btn-sm action-btn"
                                      style={{ background: "#dc3545", borderColor: "#dc3545", color: "#fff" }}
                                      title="Sil" onClick={() => { setDeleteTarget(a); setDeleteModalOpen(true) }}>
                                      <i className="bx bx-trash" />
                                    </button>
                                  </div>
                                </Td>
                              </Tr>
                            )
                          }) : (
                            <Tr>
                              <Td colSpan={7} className="text-center py-5">
                                <i className="bx bx-broadcast text-muted" style={{ fontSize: "2.5rem" }} />
                                <h6 className="mt-3 text-muted">Duyuru bulunamadı</h6>
                                <p className="text-muted small mb-0">
                                  {searchTerm ? "Arama kriterinizi değiştirmeyi deneyin." : "Sağ üstteki \"Yeni Duyuru\" ile ilk duyurunuzu ekleyin."}
                                </p>
                              </Td>
                            </Tr>
                          )}
                        </Tbody>
                      </Table>
                    </div>
                  </>
                )}
              </CardBody>
            </TableCard>
          </Col>
        </Row>
      </div>

      {/* ── Oluştur / Düzenle ─────────────────────────────────────────── */}
      <Modal isOpen={modalOpen} toggle={closeModal} centered size="lg">
        <ModalHeader toggle={closeModal}>
          <i className="bx bx-broadcast me-2" style={{ color: NAVY }} />
          {editing ? "Duyuruyu Düzenle" : "Yeni Duyuru"}
        </ModalHeader>
        <ModalBody>
          {submitError && (
            <Alert color="danger" className="rounded-4">
              <i className="bx bx-error-circle me-2" />{submitError}
            </Alert>
          )}

          {/* Panelde nasıl görüneceğinin küçük önizlemesi — süper admin
              kaydetmeden önce ikon/önem seçiminin sonucunu görsün. */}
          <div className="mb-3 p-2 rounded-4 d-flex align-items-center gap-2"
            style={{ background: "#ffe5ba", border: "1px solid #FFA500" }}>
            <div className="rounded-3 d-flex align-items-center justify-content-center"
              style={{ width: 28, height: 28, background: "#fff", flexShrink: 0 }}>
              <i className={`bx ${previewIcon}`} style={{ color: previewAccent, fontSize: "1rem" }} />
            </div>
            <div className="text-truncate" style={{ fontSize: ".82rem" }}>
              <span className="fw-bold" style={{ color: "#0A316E" }}>{formik.values.title || "Başlık"}</span>{" "}
              <span style={{ color: "rgba(10,49,110,.72)" }}>{formik.values.body || "Duyuru metni"}</span>
            </div>
          </div>

          <Row>
            <Col md={12}>
              <FormGroup>
                <Label>Başlık <span className="text-danger">*</span></Label>
                <Input name="title" value={formik.values.title} onChange={formik.handleChange} onBlur={formik.handleBlur}
                  invalid={!!err("title")} maxLength={150} placeholder="Yeni özellikler kullanıma açıldı" />
                <FormFeedback>{formik.errors.title}</FormFeedback>
                <div className="text-muted small mt-1">{formik.values.title.length}/150</div>
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup>
                <Label>Duyuru Metni <span className="text-danger">*</span></Label>
                <Input type="textarea" rows={3} name="body" value={formik.values.body} onChange={formik.handleChange}
                  onBlur={formik.handleBlur} invalid={!!err("body")} maxLength={1000}
                  placeholder="Tahsilat süreçlerinizi yeni raporlama özellikleriyle takip edebilirsiniz." />
                <FormFeedback>{formik.errors.body}</FormFeedback>
                <div className="text-muted small mt-1">{formik.values.body.length}/1000</div>
              </FormGroup>
            </Col>

            <Col md={5}>
              <FormGroup>
                <Label>Önem Derecesi <span className="text-danger">*</span></Label>
                <Select
                  options={severityOptions}
                  value={severityOptions.find((o) => o.value === formik.values.severity) || null}
                  onChange={(o) => formik.setFieldValue("severity", o?.value)}
                  placeholder="Seçiniz"
                />
              </FormGroup>
            </Col>
            <Col md={7}>
              <FormGroup>
                <Label>İkon</Label>
                {/* Seçim görsel: serbest metin alanı süper admini ikon adını
                    ezberlemeye zorluyordu ve projedeki boxicons seti eski
                    olduğu için var olmayan adlar boş kare çiziyordu. Katalog
                    ANNOUNCEMENT_ICON_GROUPS'ta ve adları doğrulanmış durumda. */}
                <div className="icon-picker">
                  <button
                    type="button"
                    className={`icon-choice${!formik.values.icon ? " is-selected" : ""}`}
                    style={!formik.values.icon
                      ? { borderColor: previewAccent, background: `${previewAccent}12` }
                      : undefined}
                    onClick={() => formik.setFieldValue("icon", "")}
                    title="Otomatik — önem derecesine göre"
                  >
                    <i className="bx bx-sync" style={{ fontSize: "1rem", color: previewAccent, opacity: .7 }} />
                  </button>
                  <div className="icon-sep" />

                  {ANNOUNCEMENT_ICON_CHOICES.map((name) => {
                    const selected = formik.values.icon === name
                    return (
                      <button
                        key={name}
                        type="button"
                        className={`icon-choice${selected ? " is-selected" : ""}`}
                        style={selected ? { borderColor: previewAccent, background: `${previewAccent}12` } : undefined}
                        onClick={() => formik.setFieldValue("icon", name)}
                        title={name}
                      >
                        <i className={`bx ${name}`} style={{ fontSize: "1rem", color: selected ? previewAccent : "#6b7280" }} />
                      </button>
                    )
                  })}
                </div>
                <div className="text-muted small mt-1">
                  {formik.values.icon
                    ? <code>{formik.values.icon}</code>
                    : <>Otomatik: <code>{ANNOUNCEMENT_DEFAULT_ICONS[previewSeverity]}</code></>}
                </div>
                {err("icon") && <div className="text-danger small mt-1">{formik.errors.icon}</div>}
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label>Buton Etiketi</Label>
                <Input name="actionLabel" value={formik.values.actionLabel} onChange={formik.handleChange}
                  onBlur={formik.handleBlur} invalid={!!err("actionLabel")} maxLength={60} placeholder="Detay" />
                <FormFeedback>{formik.errors.actionLabel}</FormFeedback>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Buton Adresi</Label>
                <Input name="actionUrl" value={formik.values.actionUrl} onChange={formik.handleChange}
                  onBlur={formik.handleBlur} invalid={!!err("actionUrl")} placeholder="/dashboard veya https://..." />
                <FormFeedback>{formik.errors.actionUrl}</FormFeedback>
                <div className="text-muted small mt-1">
                  "/" ile başlarsa panel içinde açılır, http(s) ise yeni sekmede. İkisi de boşsa buton çizilmez.
                </div>
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label>Yayın Başlangıcı</Label>
                <DateTimeInput
                  value={formik.values.startsAt}
                  onChange={(v) => formik.setFieldValue("startsAt", v)}
                />
                <div className="text-muted small mt-1">Boşsa hemen yayına girer.</div>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Yayın Bitişi</Label>
                <DateTimeInput
                  value={formik.values.endsAt}
                  onChange={(v) => {
                    formik.setFieldTouched("endsAt", true, false);
                    formik.setFieldValue("endsAt", v);
                  }}
                  invalid={!!err("endsAt")}
                />
                <FormFeedback>{formik.errors.endsAt}</FormFeedback>
                <div className="text-muted small mt-1">Boşsa süresiz yayında kalır.</div>
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label>Hedef Kitle <span className="text-danger">*</span></Label>
                <Select
                  options={targetScopeOptions}
                  value={targetScopeOptions.find((o) => o.value === formik.values.targetScope) || null}
                  onChange={(o) => formik.setFieldValue("targetScope", o?.value)}
                  placeholder="Seçiniz"
                />
                <div className="text-muted small mt-1">Süper admin duyuruları panelinde görmez.</div>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Sıra</Label>
                <Input type="number" min={0} name="sortOrder" value={formik.values.sortOrder}
                  onChange={formik.handleChange} onBlur={formik.handleBlur} invalid={!!err("sortOrder")} />
                <FormFeedback>{formik.errors.sortOrder}</FormFeedback>
                <div className="text-muted small mt-1">Küçük olan önce gösterilir.</div>
              </FormGroup>
            </Col>

            <Col md={12}>
              <FormGroup check className="mt-2">
                <Input type="checkbox" id="ann-active" name="active" checked={formik.values.active}
                  onChange={formik.handleChange} />
                <Label check for="ann-active" className="ms-1">Aktif</Label>
                <div className="text-muted small">Kapalıyken tarih aralığı uygun olsa bile panelde görünmez.</div>
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" className="btn-rounded" onClick={closeModal} disabled={saving}>İptal</Button>
          {/* Reactstrap Modal portalı içinde <Form onSubmit> sorun çıkardığı için
              gönderim butondan tetiklenir (repo geneli desen). */}
          <Button className="btn-rounded btn-navy" onClick={formik.handleSubmit} disabled={saving}>
            {saving && <Spinner size="sm" className="me-2" />}
            {editing ? "Güncelle" : "Kaydet"}
          </Button>
        </ModalFooter>
      </Modal>

      <SubscriptionDeleteModal
        isOpen={deleteModalOpen}
        toggle={() => { setDeleteModalOpen(false); setDeleteTarget(null) }}
        title="Duyuru Silme Onayı"
        icon="bx-broadcast"
        message="Aşağıdaki duyuruyu kalıcı olarak silmek istediğinizden emin misiniz?"
        item={deleteTarget}
        renderInfo={(a) => (
          <div className="d-flex justify-content-between align-items-center">
            <div style={{ minWidth: 0 }}>
              <h6 className="mb-1 text-dark text-truncate">{a.title}</h6>
              <div className="text-muted small text-truncate">{a.body}</div>
            </div>
            <span className="badge border-0 ms-2 flex-shrink-0" style={{
              borderRadius: 999,
              backgroundColor: a.visibleNow ? "#71C30B" : (a.active ? "#B0B7C3" : "#E07A7A"),
              color: "#fff",
            }}>
              {a.visibleNow ? "Yayında" : (a.active ? "Süresi dışında" : "Pasif")}
            </span>
          </div>
        )}
        onConfirm={handleDelete}
      />
    </div>
  )
}

export default Announcements
